import { NavLink, useNavigate } from "react-router-dom";
import { FaTachometerAlt, FaShieldAlt, FaLaptop, FaInfoCircle, FaSignOutAlt, FaHistory, FaBars, FaTimes, FaCheckCircle, FaUser, FaCog, FaLightbulb, FaRocket } from "react-icons/fa";
import { FaUsersLine } from "react-icons/fa6";
import { MdDataUsage } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useSidebar } from "../App";
import "../styles/sidebar.css";
import { getApiUrl, getCsrfToken, clearCsrfToken, clearUserId } from "../utils/apiHelper";

export default function Sidebar() {
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar();

  const handleLogout = async () => {
    try {
      // Get CSRF token
      const csrfToken = await getCsrfToken();
      
      // Call logout API to clear server-side session
      await fetch(getApiUrl('/api/internal/auth/logout/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include', // Include cookies for session
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear CSRF token cache
      clearCsrfToken();
      // Clear cached user ID
      clearUserId();
      // Clear any remaining localStorage (for backward compatibility)
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const menuItemsBeforeConfig = [
    { path: "/", name: t("dashboard"), icon: <FaTachometerAlt /> },
    { path: "/quick-start", name: t("quickStart"), icon: <FaRocket /> },
    { path: "/status", name: t("status"), icon: <FaCheckCircle /> },
    { path: "/reports", name: t("reports"), icon: <FaHistory /> },
  ];

  const configItems = [
    { path: "/targets", name: t("targets"), icon: <MdDataUsage /> },
    { path: "/profiles", name: t("profiles"), icon: <FaShieldAlt /> },
  ];

  const menuItemsAfterConfig = [
    { path: "/generate", name: t("generate"), icon: <FaLaptop /> },
    { path: "/employees", name: t("employees"), icon: <FaUsersLine /> },
    { path: "/account", name: t("account"), icon: <FaUser /> },
    { path: "/help", name: t("help"), icon: <FaInfoCircle /> },
    { path: "/example-training-prompts", name: t("exampleTrainingPrompts"), icon: <FaLightbulb /> },
  ];

  const logoutLabel = t("logout");
  const configurationLabel = t("configuration");

  return (
    <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
      {/* Toggle Button */}
      <button 
        className="sidebar-toggle"
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        title={sidebarCollapsed ? t("expandSidebar") : t("collapseSidebar")}
      >
        {sidebarCollapsed ? <FaBars /> : <FaTimes />}
      </button>
      
      <nav className="menu">
        {menuItemsBeforeConfig.map((item, index) => (
          <NavLink 
            to={item.path} 
            key={index} 
            className="menu-item"
            activeclassname="active"
            title={sidebarCollapsed ? item.name : ""}
          >
            <span className="icon">{item.icon}</span>
            {!sidebarCollapsed && <span className="text">{item.name}</span>}
          </NavLink>
        ))}

        {/* Configuration section - always visible */}
        <div className={`sidebar-group ${sidebarCollapsed ? "collapsed" : ""}`}>
          <div className="menu-item sidebar-group-label" title={sidebarCollapsed ? configurationLabel : ""}>
            <span className="icon"><FaCog /></span>
            {!sidebarCollapsed && <span className="text">{configurationLabel}</span>}
          </div>
          <div className="sidebar-group-items">
            {configItems.map((item, index) => (
              <NavLink
                to={item.path}
                key={index}
                className="menu-item dropdown-item"
                activeclassname="active"
                title={sidebarCollapsed ? item.name : ""}
              >
                <span className="icon">{item.icon}</span>
                {!sidebarCollapsed && <span className="text">{item.name}</span>}
              </NavLink>
            ))}
          </div>
        </div>

        {menuItemsAfterConfig.map((item, index) => (
          <NavLink 
            to={item.path} 
            key={index} 
            className="menu-item"
            activeclassname="active"
            title={sidebarCollapsed ? item.name : ""}
          >
            <span className="icon">{item.icon}</span>
            {!sidebarCollapsed && <span className="text">{item.name}</span>}
          </NavLink>
        ))}
        
        <button 
          onClick={handleLogout}
          className="menu-item logout-button"
          title={sidebarCollapsed ? logoutLabel : ""}
        >
          <span className="icon"><FaSignOutAlt /></span>
          {!sidebarCollapsed && <span className="text">{logoutLabel}</span>}
        </button>
      </nav>
    </div>
  );
}
